import { sessionStorage } from '@/infra/storage'
import { Id } from '@/infra/id'
import env from '@/infra/env'
import {
  CreateQuickBooksPlatformConnectionFromOAuth2CodeRequest,
  CreateQuickBooksPlatformConnectionRequest,
  CreateXeroPlatformConnectionFromOAuth2CodeRequest,
  ListConnectedQuickBooksAccountsRequest,
  ListConnectedXeroAccountsRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import type { BookkeepingApi } from '@/features/bookkeeping/api/bookkeeping-api'
import { collection } from '@/infra/collection'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import {
  OtherAccount,
  QuickBooksAccount,
  XeroAccount,
  type PlatformConnection,
  type PlatformConnectionTypeProps,
} from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnection'
import {
  mapQuickBooksAccountToProps,
  mapXeroAccountToProps,
} from '@/features/bookkeeping/platform-connections/bookkeeping-platform-connection-mappers'
import { createUrlWithParams } from '@/utils/url-util'
import { randomUUID } from '@/utils/uuid-util'

/**
 * The payload for the {@link BookkeepingPlatformConnectionStore.createPlatformConnectionFromOAuth2Code} method.
 */
export type CreatePlatformConnectionOAuth2Payload =
  | CreateQuickBooksOAuth2Payload
  | CreateXeroOAuth2Payload

interface CreateQuickBooksOAuth2Payload {
  type: 'QuickBooks'
  workspaceId: Id
  code: string
  realmId: string
}

interface CreateXeroOAuth2Payload {
  type: 'Xero'
  workspaceId: Id
  code: string
}

interface PlatformConnectionState {
  localReturnUrl: string
}

export class BookkeepingPlatformConnectionStore {
  private readonly _baseUrl = `${window.location.protocol}//${window.location.host}`

  private readonly _quickBooksRedirectUri = `${this._baseUrl}/bookkeeping/auth/quickbooks`

  private readonly _xeroRedirectUri = `${this._baseUrl}/bookkeeping/auth/xero`

  /**
   * The {@link QuickBooksAccount} collection.
   */
  quickBooksAccounts = collection({
    create: () => new QuickBooksAccount(),
    getDataId: (d) => d.connectionId,
    getModelId: (m) => m.connectionId,
  })

  /**
   * The {@link XeroAccount} collection.
   */
  xeroAccounts = collection({
    create: () => new XeroAccount(),
    getDataId: (d) => d.connectionId,
    getModelId: (m) => m.connectionId,
  })

  constructor(private readonly _api: BookkeepingApi) {}

  /**
   * Sets the platform connection given the props.
   */
  setPlatformConnection(
    props: PlatformConnectionTypeProps,
  ): PlatformConnection {
    switch (props.type) {
      case 'QuickBooks':
        return this.quickBooksAccounts.set(props.quickBooks)
      case 'Xero':
        return this.xeroAccounts.set(props.xero)
      case 'Other':
        return new OtherAccount().set(props.other)
      default:
        throw new Error('Unsupported platform connection type')
    }
  }

  /**
   * Lists {@link QuickBooksAccount} platform connections.
   */
  async listQuickBooksPlatformConnections(
    query: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<QuickBooksAccount>> {
    signal?.throwIfAborted()

    const request = new ListConnectedQuickBooksAccountsRequest()
    if (query.after) {
      request.setPageToken(query.after)
    }

    if (query.limit) {
      request.setPageSize(query.limit)
    }

    const response =
      await this._api.quickBooks.listConnectedQuickBooksAccounts(request)

    const accounts = response
      .getConnectedQuickbooksAccountsList()
      .map(mapQuickBooksAccountToProps)

    return {
      data: this.quickBooksAccounts.set(accounts.map((x) => x.quickBooks)),
      next: response.getNextPageToken(),
    }
  }

  /**
   * Lists {@link XeroAccount} platform connections.
   */
  async listXeroPlatformConnections(
    query: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<XeroAccount>> {
    signal?.throwIfAborted()

    const request = new ListConnectedXeroAccountsRequest()
    if (query.after) {
      request.setPageToken(query.after)
    }

    if (query.limit) {
      request.setPageSize(query.limit)
    }

    const response = await this._api.xero.listConnectedXeroAccounts(request)

    const accounts = response
      .getConnectedXeroAccountsList()
      .map(mapXeroAccountToProps)

    return {
      data: this.xeroAccounts.set(accounts.map((x) => x.xero)),
      next: response.getNextPageToken(),
    }
  }

  /**
   * Creates {@link QuickBooksAccount}.
   */
  async createQuickBooksPlatformConnection(
    workspaceId: Id,
    companyId: string,
    companyName: string,
  ): Promise<QuickBooksAccount> {
    const request = new CreateQuickBooksPlatformConnectionRequest()

    request.setRealmId(companyId)
    request.setCompanyName(companyName)
    request.setWorkspaceId(Id.assert(workspaceId))

    const connection = await this._api.quickBooks
      .createQuickBooksPlatformConnection(request)
      .then((c) => c.toObject())

    const quickBooksAccount = this.setPlatformConnection({
      type: 'QuickBooks',
      quickBooks: {
        connectionId: connection.id,
        realmId: companyId,
        companyName: companyName,
      },
    })

    return quickBooksAccount as QuickBooksAccount
  }

  /**
   * Creates a platform connection from OAuth2 code.
   */
  createPlatformConnectionFromOAuth2Code(
    platformConnectionPayload: CreatePlatformConnectionOAuth2Payload,
  ) {
    switch (platformConnectionPayload.type) {
      case 'QuickBooks':
        return this._createQuickBooksPlatformConnectionFromOAuth2Code(
          platformConnectionPayload,
        )
      case 'Xero':
        return this._createXeroPlatformConnectionFromOAuth2Code(
          platformConnectionPayload,
        )
      default:
        throw new Error('Unsupported platform connection type')
    }
  }

  /**
   * Gets the QuickBooks OAuth2 state.
   */
  getQuickBooksSecurityState(state: string): PlatformConnectionState | null {
    return this._consumeOAuthState(state)
  }

  /**
   * Redirects the user to the QuickBooks OAuth2 authorization page.
   */
  redirectToQuickBooksOauth2Page(localReturnUrl: URL) {
    const scope = [
      'com.intuit.quickbooks.accounting',
      'openid',
      'profile',
      'email',
      'phone',
      'address',
    ].join(' ')

    const state = randomUUID()
    const statePayload = {
      localReturnUrl: localReturnUrl.toString(),
    }

    sessionStorage.setItem(state, JSON.stringify(statePayload))

    const url = createUrlWithParams(
      'https://appcenter.intuit.com/connect/oauth2',
      {
        response_type: 'code',
        client_id: env.QUICK_BOOKS_CLIENT_ID,
        scope: scope,
        redirect_uri: this._quickBooksRedirectUri,
        state: state,
      },
    )

    window.location.href = url.toString()
  }

  /**
   * Gets the Xero OAuth2 state.
   */
  getXeroSecurityState(state: string): PlatformConnectionState | null {
    return this._consumeOAuthState(state)
  }

  /**
   * Redirects the user to the Xero OAuth2 authorization page.
   */
  redirectToXeroOauth2Page(localReturnUrl: URL) {
    const scope = [
      'openid',
      'profile',
      'email',
      'accounting.transactions',
    ].join(' ')

    const state = randomUUID()
    const statePayload = {
      localReturnUrl: localReturnUrl.toString(),
    }

    sessionStorage.setItem(state, JSON.stringify(statePayload))

    const url = createUrlWithParams(
      'https://login.xero.com/identity/connect/authorize',
      {
        response_type: 'code',
        client_id: env.XERO_CLIENT_ID,
        scope: scope,
        redirect_uri: this._xeroRedirectUri,
        state,
      },
    )

    window.location.href = url.toString()
  }

  /**
   * Creates {@link XeroAccount} from OAuth2 code.
   */
  private async _createXeroPlatformConnectionFromOAuth2Code(
    payload: CreateXeroOAuth2Payload,
  ) {
    const request = new CreateXeroPlatformConnectionFromOAuth2CodeRequest()
      .setCode(payload.code)
      .setRedirectUri(this._xeroRedirectUri)
      .setWorkspaceId(Id.assert(payload.workspaceId))

    const proto =
      await this._api.xero.createXeroPlatformConnectionFromOAuth2Code(request)

    await this.listXeroPlatformConnections({ limit: 1, after: null })

    return this.xeroAccounts.get(proto.getId())!
  }

  /**
   * Creates {@link QuickBooksAccount} from OAuth2 code.
   */
  private async _createQuickBooksPlatformConnectionFromOAuth2Code(
    payload: CreateQuickBooksOAuth2Payload,
  ) {
    const request =
      new CreateQuickBooksPlatformConnectionFromOAuth2CodeRequest()
        .setRealmId(payload.realmId)
        .setCode(payload.code)
        .setRedirectUri(this._quickBooksRedirectUri)
        .setWorkspaceId(Id.assert(payload.workspaceId))

    const proto =
      await this._api.quickBooks.createQuickBooksPlatformConnectionFromOAuth2Code(
        request,
      )

    await this.listQuickBooksPlatformConnections({ limit: 1, after: null })

    return this.quickBooksAccounts.get(proto.getId())!
  }

  /**
   * Consumes the platform connection state from session storage.
   */
  private _consumeOAuthState(key: string): PlatformConnectionState | null {
    const payload = sessionStorage.getItem(key)

    sessionStorage.removeItem(key)

    if (!payload) {
      return null
    }

    return JSON.parse(payload) as PlatformConnectionState
  }
}
